// src/pages/Grow/Intakes/Components/RejectIntakeModal.tsx
/**
 * Rejecting a course intake. A reason is required, and the submit stays disabled until one is given.
 *
 * ⚠️ Close to `RejectRequestModal` but not the same, and they must not be merged. Both carry a
 * required `reason`, but the notification flags differ: this one sends `sendNotification` (plainly
 * "tell the applicant or not"), while the care side sends `sendReferralMessage`, which also points the
 * requester at other support. Different payloads, different meaning, different copy.
 */
import { useEffect, useState } from 'react';
import { Alert, Button, Group, Modal, Stack, Switch, Text, Textarea } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { toast } from '@/components/ui/providers/AlertProvider';
import { useRegistrationIntakeService, type RegistrationIntakeModelView } from '@actschurch/ccms-sdk';

export interface RejectIntakeModalProps {
  intake: RegistrationIntakeModelView | null;
  opened: boolean;
  onClose: () => void;
  onRejected: () => void;
}

export default function RejectIntakeModal({
  intake, opened, onClose, onRejected,
}: RejectIntakeModalProps) {
  const intakeService = useRegistrationIntakeService();

  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  /** Defaults to true, matching the SDK's own default for `sendNotification`. */
  const [sendNotification, setSendNotification] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!opened) return;
    setReason('');
    setTouched(false);
    setSendNotification(true);
  }, [opened]);

  const handleReject = async () => {
    const trimmed = reason.trim();
    if (!trimmed) { setTouched(true); return; }
    if (!intake || busy) return;

    setBusy(true);
    try {
      await intakeService.reject(intake.intakeID, { reason: trimmed, sendNotification });
      toast.success('Intake rejected', `${intake.reference ?? `Intake #${intake.intakeID}`} was closed.`);
      onRejected();
      onClose();
    } catch (e) {
      console.error('[RejectIntake] reject failed:', e);
      toast.error('Could not reject', e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const participantCount = intake?.participants?.length ?? 0;

  return (
    <Modal
      opened={opened}
      onClose={busy ? () => {} : onClose}
      title="Reject intake"
      size="lg"
      centered
    >
      {intake && (
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {intake.reference ?? `Intake #${intake.intakeID}`} from{' '}
            <Text span fw={600}>{intake.fullName ?? 'Unknown'}</Text> for{' '}
            <Text span fw={600}>{intake.courseName ?? 'a course'}</Text>. No registration will be
            created
            {participantCount > 1 ? `, for any of the ${participantCount} people on it.` : '.'}
          </Text>

          <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
            <Textarea
              label="Reason"
              placeholder="Why is this intake not being taken up?"
              autosize
              minRows={3}
              data-autofocus
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
              onBlur={() => setTouched(true)}
              error={touched && !reason.trim() ? 'A reason is required to reject.' : undefined}
            />
          </Alert>

          <Switch
            label="Let the applicant know their intake was declined"
            checked={sendNotification}
            onChange={(e) => setSendNotification(e.currentTarget.checked)}
            description={sendNotification
              ? 'They will be emailed that their sign-up was not taken up.'
              : 'Nothing is sent. Use this for an obvious duplicate or a withdrawal they already know about.'}
          />

          <Text size="xs" c="dimmed">
            The reason is stored on the intake for the Grow office. It is not included in the message
            sent to the applicant.
          </Text>

          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button color="red" onClick={handleReject} loading={busy} disabled={!reason.trim()}>
              Reject intake
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
